import { AuthUser, LocalAuthData, UserAccount } from '../models/authModels';

export type globalType = {
  localDataService?: LocalDataService;
};

const varnames = {
  auth: 'auth',
  userId: 'userId',
  uid: 'uid',
  companyId: 'companyId',
  portalHost: 'portalHost',
  portalUser: 'portalUser',
  campaignId: 'campaignId',
  ssoError: 'ssoError',
};

class LocalDataService {
  constructor() {
    if ((global as globalType).localDataService) {
      throw new Error('New instance cannot be created.');
    } else {
    }
    (global as globalType).localDataService = this;
  }

  isBrowser() {
    return typeof window !== 'undefined';
  }

  getInstance() {
    return this;
  }

  setUserSessionData(value: AuthUser) {
    return window.sessionStorage.setItem(varnames.portalUser, JSON.stringify(value));
  }

  getUserSessionData() {
    if (!this.isBrowser()) return null;
    const user = window.sessionStorage.getItem(varnames.portalUser);
    return user ? JSON.parse(user).account : {};
  }

  saveSSOError(message: string) {
    if (!this.isBrowser()) return null;
    return window.sessionStorage.setItem(varnames.ssoError, message);
  }

  popSSOError(): string | null {
    if (!this.isBrowser()) return null;
    const message = window.sessionStorage.getItem(varnames.ssoError);
    if (message) window.sessionStorage.removeItem(varnames.ssoError);
    return message;
  }

  // TODO: Improve by setting storage type as argument: local || session
  set(key: string, value: string) {
    if (!this.isBrowser()) return null;

    return window.localStorage.setItem(key, value);
  }

  get<T>(key: string) {
    if (!this.isBrowser()) return null;

    return window.localStorage.getItem(key) as T;
  }

  saveCampaignId(campaignId: Number) {
    if (!this.isBrowser()) return null;

    return this.set(varnames.campaignId, campaignId.toString());
  }

  getCampaignId(): Number {
    return Number(this.get(varnames.campaignId));
  }

  saveAuth(token: string) {
    return this.set(varnames.auth, token);
  }

  getAuth(): string {
    return this.get(varnames.auth) as string;
  }

  saveUserId(userId: number) {
    return this.set(varnames.userId, userId.toString());
  }

  getUserId(): number {
    return Number(this.get(varnames.userId));
  }

  saveUid(uid: number) {
    return this.set(varnames.uid, uid.toString());
  }

  getUid(): number {
    return Number(this.get(varnames.uid));
  }

  saveCompanyId(companyId: number) {
    return this.set(varnames.companyId, companyId?.toString());
  }

  getCompanyId(): number {
    return Number(this.get(varnames.companyId));
  }

  savePortalHost(host: string) {
    return this.set(varnames.portalHost, host);
  }

  getPortalHost(): string {
    return this.get(varnames.portalHost) as string;
  }

  getAuthObject(): LocalAuthData {
    return {
      companyId: this.get(varnames.companyId),
      auth: this.get(varnames.auth),
      userId: this.get(varnames.userId),
      uid: this.get(varnames.uid),
      host: this.get(varnames.portalHost),
    } as LocalAuthData;
  }

  clearData() {
    const persistData = ['companyId', 'portalHost'];
    Object.entries(varnames).forEach((k) => {
      if (!persistData.includes(k[1])) this.set(k[1], '');
    });
  }
}

let localDataServiceSingleton;
if (!(global as globalType).localDataService) localDataServiceSingleton = new LocalDataService();
else localDataServiceSingleton = (global as globalType).localDataService;
export default localDataServiceSingleton as LocalDataService;
